import {
  dashboardPlatformKeyFromDescription,
  socialNetworkTypeIdToDashboardPlatformKey,
} from "./socialNetworkCatalog"
import { formatLocalDateKey } from "../../utils/dateRangeUtils"

/** Date-only param for messages/alerts report APIs (e.g. startDate=2026-08-01&endDate=2026-08-31). */
export function formatMessagesReportBoundaryDate(date) {
  return formatLocalDateKey(date) || ""
}

/** Date-only param for GET …/report/clients/{id}/heatmap?start_date=&end_date=. */
export function formatPostHeatmapReportDate(date) {
  return formatMessagesReportBoundaryDate(date)
}

export function buildMessagesReportFetchKey(clientId, startDate, endDate) {
  return `${String(clientId ?? "")}|${startDate ?? ""}|${endDate ?? ""}`
}

/** Platforms shown on Messages / Sentiment monitoring charts. */
export const MESSAGES_PLATFORM_CHART_KEYS = [
  "instagram",
  "facebook",
  "linkedin",
  "twitter",
  "tiktok",
]

const CHART_KEY_TO_LABEL = {
  instagram: "Instagram",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  twitter: "X",
  tiktok: "TikTok",
}

function descriptionToChartKey(description) {
  return dashboardPlatformKeyFromDescription(description)
}

/** Map API socialNetworkTypeId → chart key, or null if not a supported platform. */
export function socialNetworkTypeIdToChartKey(socialNetworkTypes, typeId) {
  return socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes)
}

/** Connected property social accounts → dashboard chart platform keys. */
export function connectedChartKeysFromSocialAccounts(accounts, socialNetworkTypes) {
  const keys = new Set()
  if (!Array.isArray(accounts)) return keys

  for (const account of accounts) {
    const typeId =
      account?.socialNetworkType?.id ??
      account?.socialNetworkTypeId ??
      account?.social_network_type_id

    const fromTypeId = socialNetworkTypeIdToChartKey(socialNetworkTypes, typeId)
    if (fromTypeId) {
      keys.add(fromTypeId)
      continue
    }

    const desc =
      account?.socialNetworkType?.description ??
      account?.socialNetworkType?.name ??
      account?.social_network_type?.description ??
      ""
    const fromDesc = descriptionToChartKey(desc)
    if (fromDesc) keys.add(fromDesc)
  }

  return keys
}

/** Stable display order for connected platform keys. */
export function orderedConnectedChartKeys(connectedKeys) {
  const set =
    connectedKeys instanceof Set ? connectedKeys : new Set(connectedKeys || [])
  return MESSAGES_PLATFORM_CHART_KEYS.filter((key) => set.has(key))
}

function chartKeyToLabel(chartKey) {
  return CHART_KEY_TO_LABEL[chartKey] || null
}

export function enumerateDays(startDate, endDate) {
  const start = startDate instanceof Date ? startDate : new Date(startDate)
  const end = endDate instanceof Date ? endDate : new Date(endDate)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return []

  const days = []
  const cursor = new Date(start)
  cursor.setHours(0, 0, 0, 0)
  const endDay = new Date(end)
  endDay.setHours(0, 0, 0, 0)

  while (cursor <= endDay) {
    const y = cursor.getFullYear()
    const m = String(cursor.getMonth() + 1).padStart(2, "0")
    const day = String(cursor.getDate()).padStart(2, "0")
    days.push(`${y}-${m}-${day}`)
    cursor.setDate(cursor.getDate() + 1)
  }
  return days
}

/**
 * @param {Array<{ messageDay?: string, messageCount?: number, socialNetworkTypeId?: number }>} rows
 * @param {Array<{ id?: number, description?: string, name?: string }>} socialNetworkTypes
 * @param {{
 *   startDate?: Date,
 *   endDate?: Date,
 *   platformKeysFilter?: string[] | null,
 *   channelLabelFilter?: string | null,
 * }} options
 */
export function buildMonitoringChartsFromMessagesRows(
  rows,
  socialNetworkTypes = [],
  options = {},
) {
  const safeRows = Array.isArray(rows) ? rows : []
  const { startDate, endDate, platformKeysFilter, channelLabelFilter } = options

  const totalsByDay = new Map()
  const totalsByKey = new Map()
  const platformKeysSet = new Set()

  for (const row of safeRows) {
    const day = String(row?.messageDay ?? "").slice(0, 10)
    if (!day) continue

    const chartKey = socialNetworkTypeIdToChartKey(
      socialNetworkTypes,
      row?.socialNetworkTypeId,
    )
    if (!chartKey) continue

    const label = chartKeyToLabel(chartKey)
    const count = Number(row?.messageCount) || 0

    if (platformKeysFilter?.length && !platformKeysFilter.includes(chartKey)) continue
    if (channelLabelFilter && channelLabelFilter !== "all" && label !== channelLabelFilter) {
      continue
    }

    platformKeysSet.add(chartKey)

    if (!totalsByDay.has(day)) totalsByDay.set(day, {})
    const dayBucket = totalsByDay.get(day)
    dayBucket[chartKey] = (dayBucket[chartKey] || 0) + count

    totalsByKey.set(chartKey, (totalsByKey.get(chartKey) || 0) + count)
  }

  const platformKeys = MESSAGES_PLATFORM_CHART_KEYS.filter((key) =>
    platformKeysSet.has(key),
  )
  const dayKeys =
    startDate && endDate
      ? enumerateDays(startDate, endDate)
      : [...totalsByDay.keys()].sort()

  const monitoringSeries = dayKeys.map((date) => {
    const dayBucket = totalsByDay.get(date) || {}
    const point = { date }
    for (const key of platformKeys) {
      point[key] = dayBucket[key] || 0
    }
    return point
  })

  const monitoringShare = platformKeys.map((key) => ({
    name: chartKeyToLabel(key),
    value: totalsByKey.get(key) || 0,
  }))

  return {
    monitoringSeries,
    monitoringShare,
    platformKeys,
  }
}
